/**
 * Post-login return-to. Deep links (email, push, universal links) that hit a
 * protected route while signed out are remembered here so every sign-in
 * path (password, 2FA, passkey, native Face ID, welcome-back) can land the
 * user on the page they were actually going to.
 */
const KEY = 'akf_post_login_redirect';
const BLOCKED = ['/auth', '/login', '/signup', '/register', '/logout', '/verify-email', '/reset-password'];

export const safePath = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  const path = raw.split('?')[0].split('#')[0];
  if (path === '/' || BLOCKED.some((b) => path === b || path.startsWith(`${b}/`))) return null;
  return raw;
};

export const rememberRedirect = (raw) => {
  const p = safePath(raw);
  if (!p) return;
  try { sessionStorage.setItem(KEY, p); } catch { /* noop */ }
};

export const peekRedirect = () => {
  try { return safePath(sessionStorage.getItem(KEY)); } catch { return null; }
};

export const consumeRedirect = () => {
  const p = peekRedirect();
  try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
  return p;
};

/** Where to go once a session exists. Admin / personal homes win over deep links. */
export const postLoginTarget = (user, fallback = '/dashboard') => {
  if (user?.is_platform_admin) { consumeRedirect(); return '/admin'; }
  const target = consumeRedirect();
  if (target) return target;
  if (user?.account_type === 'personal') return '/personal';
  return fallback;
};

/** `/auth?redirect=<current page>` for hard (window.location) bounces on session expiry. */
export const authUrlWithReturnTo = () => {
  try {
    const here = `${window.location.pathname}${window.location.search || ''}`;
    const p = safePath(here);
    if (!p) return '/auth';
    rememberRedirect(p);
    return `/auth?redirect=${encodeURIComponent(p)}`;
  } catch { return '/auth'; }
};
